import React, { useEffect, useState } from "react";
import { IDoctor, ITreatment, ISubTreatment } from "../types/clinic.types";
import { ConfirmationModal } from "./ConfirmationModal";
import { TreatmentCard, CatalogTreatment } from "./TreatmentCard";
import { SubTreatmentFormModal } from "./SubTreatmentFormModal";
import baseUrl from "../../../../api/baseUrl";
import { toast } from "react-toastify";

interface Props {
  treatments: ITreatment[];
  onTreatmentsChange: (treatments: ITreatment[]) => void;
  doctors: IDoctor[];
  onDoctorsChange: (doctors: IDoctor[]) => void;
}

/** Assignment may store the bookTreatment row id or the catalog id (see Employee model). */
function treatmentRowMatches(storedId: string, t: ITreatment) {
  return (
    String(storedId) === String(t.id) ||
    (t.treatmentId != null && String(storedId) === String(t.treatmentId))
  );
}

function isDentistAssigned(doctor: IDoctor, treatment: ITreatment) {
  return (doctor.assignedTreatmentIds || []).some((id) =>
    treatmentRowMatches(String(id), treatment)
  );
}

function extractNewBookTreatmentId(
  responseData: { bookTreatment?: { _id?: string; treatmentId?: string | { _id?: string } }[] },
  catalogTreatmentId: string
): string | undefined {
  const rows = responseData?.bookTreatment ?? [];
  const match = rows.find((row) => {
    const tid = row.treatmentId;
    const tidStr =
      typeof tid === "object" && tid !== null && "_id" in tid
        ? String(tid._id)
        : String(tid ?? "");
    return tidStr === String(catalogTreatmentId);
  });
  return match?._id ? String(match._id) : rows[rows.length - 1]?._id?.toString();
}

function validatePrices(localPrice: string, foreginPrice: string): string | null {
  if (localPrice === "" || isNaN(Number(localPrice)) || Number(localPrice) < 0) {
    return "Local price must be a valid non-negative number.";
  }
  if (foreginPrice === "" || isNaN(Number(foreginPrice)) || Number(foreginPrice) < 0) {
    return "Foreigner price must be a valid non-negative number.";
  }
  return null;
}

export const Treatments: React.FC<Props> = ({
  treatments,
  onTreatmentsChange,
  doctors,
  onDoctorsChange,
}) => {
  const [catalog, setCatalog] = useState<CatalogTreatment[]>([]);
  const [isLoadingCatalog, setIsLoadingCatalog] = useState(true);
  const [savingCatalogId, setSavingCatalogId] = useState<string | null>(null);

  const [bulkLocal, setBulkLocal] = useState("");
  const [bulkForeign, setBulkForeign] = useState("");
  const [isBulkSaving, setIsBulkSaving] = useState(false);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);

  const [deleteCandidate, setDeleteCandidate] = useState<string | null>(null);
  const [isDeletingTreatment, setIsDeletingTreatment] = useState(false);

  const [isSubModalOpen, setIsSubModalOpen] = useState(false);
  const [editingSub, setEditingSub] = useState<ISubTreatment | null>(null);
  const [parentTreatmentId, setParentTreatmentId] = useState<string | null>(null);

  const bookableDentists = doctors.filter((d) => d.IsBookDentist);

  useEffect(() => {
    baseUrl
      .get("/client/booking/findBookClinicTreatment")
      .then((response: { data?: { data?: { _id: string; name: string; decription?: string }[] } }) => {
        setCatalog(
          (response.data?.data ?? []).map((item) => ({
            id: String(item._id),
            name: item.name,
            description: item.decription ?? "",
          }))
        );
      })
      .catch((error) => {
        console.error(error);
        toast.error("Failed to load the treatment catalog.");
      })
      .finally(() => setIsLoadingCatalog(false));
  }, []);

  const findConfigured = (catalogId: string): ITreatment | null =>
    treatments.find((t) => String(t.treatmentId ?? "") === catalogId) ?? null;

  const handleSavePrices = (
    row: CatalogTreatment,
    existing: ITreatment | null,
    localPrice: string,
    foreginPrice: string
  ) => {
    const error = validatePrices(localPrice, foreginPrice);
    if (error) {
      toast.error(error);
      return;
    }

    const payload = {
      treatmentId: row.id,
      localPrice: Number(localPrice),
      foreginPrice: Number(foreginPrice),
    };
    setSavingCatalogId(row.id);

    if (existing) {
      baseUrl
        .patch("/client/booking/updateBookTreatments", { ...payload, id: existing.id })
        .then((response) => {
          if (response.data?.success) {
            onTreatmentsChange(
              treatments.map((t) =>
                t.id === existing.id
                  ? { ...t, localPrice: payload.localPrice, foreginPrice: payload.foreginPrice }
                  : t
              )
            );
            toast.success("Prices updated!");
          } else {
            toast.error(response.data?.message || "Failed to update prices");
          }
        })
        .catch((error) => {
          console.error(error);
          toast.error(error?.response?.data?.message || "Failed to update prices");
        })
        .finally(() => setSavingCatalogId(null));
    } else {
      baseUrl
        .patch("/client/booking/createBookTreatments", payload)
        .then((response) => {
          if (response.data?.success) {
            const newId = extractNewBookTreatmentId(response.data?.data ?? {}, row.id);
            if (!newId) {
              toast.error("Treatment saved but could not read the new id. Please refresh.");
              return;
            }
            onTreatmentsChange([
              ...treatments,
              {
                id: newId,
                treatmentId: row.id,
                name: row.name,
                description: row.description,
                localPrice: payload.localPrice,
                foreginPrice: payload.foreginPrice,
                subTreatments: [],
              },
            ]);
            toast.success(`${row.name} is now offered — assign a dentist to publish it.`);
          } else {
            toast.error(response.data?.message || "Failed to add treatment");
          }
        })
        .catch((error) => {
          if (error.response?.status === 409) {
            toast.error("This treatment is already added to your clinic.");
          } else {
            console.error(error);
            toast.error(error?.response?.data?.message || "Failed to add treatment");
          }
        })
        .finally(() => setSavingCatalogId(null));
    }
  };

  /**
   * One price for every catalog treatment: updates rows the clinic already
   * offers and creates the missing ones (new rows stay hidden publicly until
   * a dentist is ticked). Sequential on purpose — small catalog, and partial
   * failures stay attributable.
   */
  const handleBulkApply = async () => {
    setBulkConfirmOpen(false);
    const localPrice = Number(bulkLocal);
    const foreginPrice = Number(bulkForeign);
    setIsBulkSaving(true);

    let next = [...treatments];
    let updated = 0;
    let added = 0;
    let failed = 0;

    for (const row of catalog) {
      const existing = next.find((t) => String(t.treatmentId ?? "") === row.id) ?? null;
      try {
        if (existing) {
          if (
            Number(existing.localPrice) === localPrice &&
            Number(existing.foreginPrice) === foreginPrice
          ) {
            continue; // already at the target price
          }
          const res = await baseUrl.patch("/client/booking/updateBookTreatments", {
            treatmentId: row.id,
            localPrice,
            foreginPrice,
            id: existing.id,
          });
          if (!res.data?.success) throw new Error(res.data?.message);
          next = next.map((t) =>
            t.id === existing.id ? { ...t, localPrice, foreginPrice } : t
          );
          updated += 1;
        } else {
          const res = await baseUrl.patch("/client/booking/createBookTreatments", {
            treatmentId: row.id,
            localPrice,
            foreginPrice,
          });
          if (!res.data?.success) throw new Error(res.data?.message);
          const newId = extractNewBookTreatmentId(res.data?.data ?? {}, row.id);
          if (!newId) throw new Error("missing new id");
          next = [
            ...next,
            {
              id: newId,
              treatmentId: row.id,
              name: row.name,
              description: row.description,
              localPrice,
              foreginPrice,
              subTreatments: [],
            },
          ];
          added += 1;
        }
      } catch (error) {
        console.error(`Bulk price failed for ${row.name}:`, error);
        failed += 1;
      }
    }

    onTreatmentsChange(next);
    setIsBulkSaving(false);

    if (failed > 0) {
      toast.error(
        `Applied to ${updated + added} treatment(s), but ${failed} failed — please retry those rows.`
      );
    } else if (updated + added === 0) {
      toast.info("All treatments already have these prices.");
    } else {
      toast.success(
        `Prices applied to all treatments (${updated} updated, ${added} newly offered).`
      );
    }
  };

  const bulkError = validatePrices(bulkLocal, bulkForeign);
  const existingCount = treatments.length;
  const newCount = catalog.filter(
    (row) => !treatments.some((t) => String(t.treatmentId ?? "") === row.id)
  ).length;

  const handleToggleDentist = (treatment: ITreatment, doctor: IDoctor) => {
    const currentIds = doctor.assignedTreatmentIds || [];
    const has = currentIds.some((id) => treatmentRowMatches(String(id), treatment));
    const newIds = has
      ? currentIds.filter((id) => !treatmentRowMatches(String(id), treatment))
      : [...currentIds, String(treatment.id)];

    const previousDoctors = doctors;
    onDoctorsChange(
      doctors.map((d) => (d.id === doctor.id ? { ...d, assignedTreatmentIds: newIds } : d))
    );

    baseUrl
      .patch("/client/booking/addTreatmentEmployee", {
        treatmentids: newIds,
        employeeId: doctor.id,
      })
      .then((res) => {
        if (!res.data?.success) {
          onDoctorsChange(previousDoctors);
          toast.error(res.data?.message || "Failed to update dentist assignment");
        }
      })
      .catch((error) => {
        console.error(error);
        onDoctorsChange(previousDoctors);
        toast.error(error?.response?.data?.message || "Failed to update dentist assignment");
      });
  };

  const handleDeleteTreatment = () => {
    if (!deleteCandidate || isDeletingTreatment) return;

    const previousTreatments = treatments;
    setIsDeletingTreatment(true);

    baseUrl
      .patch("/client/booking/deleteBookTreatments", {
        treatmentId: deleteCandidate,
      })
      .then((res) => {
        if (res.data?.success) {
          onTreatmentsChange(treatments.filter((t) => t.id !== deleteCandidate));
          setDeleteCandidate(null);
          toast.success("Treatment deleted successfully!");
        } else {
          toast.error(res.data?.message || "Failed to delete treatment");
        }
      })
      .catch((error) => {
        console.error(error);
        onTreatmentsChange(previousTreatments);
        toast.error(error?.response?.data?.message || "Failed to delete treatment");
      })
      .finally(() => setIsDeletingTreatment(false));
  };

  const closeSubModal = () => {
    setIsSubModalOpen(false);
    setEditingSub(null);
    setParentTreatmentId(null);
  };

  const handleOpenAddSub = (parentId: string) => {
    setParentTreatmentId(parentId);
    setEditingSub(null);
    setIsSubModalOpen(true);
  };

  const handleOpenEditSub = (parentId: string, sub: ISubTreatment) => {
    setParentTreatmentId(parentId);
    setEditingSub(sub);
    setIsSubModalOpen(true);
  };

  const handleSaveSubTreatment = (subData: ISubTreatment) => {
    if (!parentTreatmentId) return;

    const isEdit = !!editingSub;
    const previousTreatments = treatments;
    const tempClientId = !isEdit ? `st_${Date.now()}` : null;

    const optimisticTreatments = treatments.map((t) => {
      if (t.id !== parentTreatmentId) return t;

      let newSubTreatments: ISubTreatment[];
      if (isEdit && editingSub) {
        newSubTreatments = t.subTreatments.map((s) =>
          s.id === editingSub.id ? { ...s, ...subData, id: editingSub.id } : s
        );
      } else {
        newSubTreatments = [...t.subTreatments, { ...subData, id: tempClientId! }];
      }
      return { ...t, subTreatments: newSubTreatments };
    });

    onTreatmentsChange(optimisticTreatments);

    const endpoint = isEdit
      ? "/client/booking/updateBookSubTreatment"
      : "/client/booking/addBookSubTreatment";

    const payload = isEdit
      ? {
          bookTreatmentId: parentTreatmentId,
          subTreatmentId: editingSub!.id,
          subTreatment: subData,
        }
      : {
          bookTreatmentId: parentTreatmentId,
          subTreatment: subData,
        };

    baseUrl
      .patch(endpoint, payload)
      .then((res) => {
        if (res.data?.success) {
          const serverId = res.data?.newSubTreatmentId as string | undefined;
          if (!isEdit && serverId && tempClientId) {
            onTreatmentsChange(
              optimisticTreatments.map((t) => {
                if (t.id !== parentTreatmentId) return t;
                return {
                  ...t,
                  subTreatments: t.subTreatments.map((s) =>
                    s.id === tempClientId ? { ...s, ...subData, id: serverId } : s
                  ),
                };
              })
            );
          }
          closeSubModal();
          toast.success(
            isEdit ? "Sub-treatment updated successfully!" : "Sub-treatment added successfully!"
          );
        } else {
          onTreatmentsChange(previousTreatments);
          toast.error(res.data?.message || "Failed to save sub-treatment");
        }
      })
      .catch((error) => {
        console.error(error);
        onTreatmentsChange(previousTreatments);
        toast.error(error?.response?.data?.message || "Failed to save sub-treatment");
      });
  };

  const handleDeleteSubTreatment = (parentId: string, subId: string) => {
    if (!subId) {
      toast.error("Cannot delete sub-treatment: missing id. Please refresh.");
      return;
    }

    const previousTreatments = treatments;

    onTreatmentsChange(
      treatments.map((t) => {
        if (t.id !== parentId) return t;
        return {
          ...t,
          subTreatments: t.subTreatments.filter((s) => s.id !== subId),
        };
      })
    );

    baseUrl
      .patch("/client/booking/deleteBookSubTreatment", {
        bookTreatmentId: parentId,
        subTreatmentId: subId,
      })
      .then((res) => {
        if (res.data?.success) {
          toast.success("Sub-treatment deleted successfully!");
        } else {
          onTreatmentsChange(previousTreatments);
          toast.error(res.data?.message || "Failed to delete sub-treatment");
        }
      })
      .catch((error) => {
        console.error(error);
        onTreatmentsChange(previousTreatments);
        toast.error(error?.response?.data?.message || "Failed to delete sub-treatment");
      });
  };

  return (
    <section className="bg-white rounded-[8px] shadow-lg shadow-slate-200/50 border border-slate-100 p-3 sm:p-4">
      <div className="pb-3 mb-4 border-b border-slate-200">
        <h2 className="text-xs font-bold text-primary mb-0">Treatments</h2>
        <p className="mt-1 text-[11px] text-slate-500">
          Every treatment below comes from the BookMyDentist catalog. Add your prices to offer
          one, then tick the dentists who provide it — treatments with no dentist assigned are
          not shown on BookMyDentist.lk.
        </p>
      </div>

      {!isLoadingCatalog && catalog.length > 0 && (
        <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50/70 p-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h3 className="text-xs font-semibold text-slate-800">
                Same price for every treatment?
              </h3>
              <p className="mt-0.5 text-[11px] text-slate-500">
                Enter it once and apply to all — you can still adjust any single treatment below
                afterwards.
              </p>
            </div>
            <div className="flex items-end gap-2">
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                  Local (LKR)
                </label>
                <input
                  type="number"
                  min="0"
                  value={bulkLocal}
                  onChange={(e) => setBulkLocal(e.target.value)}
                  placeholder="e.g. 2000"
                  className="w-[110px] px-2.5 py-1.5 border border-slate-200 rounded-lg bg-white text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                  Foreigner (USD)
                </label>
                <input
                  type="number"
                  min="0"
                  value={bulkForeign}
                  onChange={(e) => setBulkForeign(e.target.value)}
                  placeholder="e.g. 20"
                  className="w-[110px] px-2.5 py-1.5 border border-slate-200 rounded-lg bg-white text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <button
                type="button"
                disabled={!!bulkError || isBulkSaving}
                onClick={() => setBulkConfirmOpen(true)}
                className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-primary text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                {isBulkSaving ? "Applying…" : "Apply to all"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-4">
        {isLoadingCatalog ? (
          <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/50 py-10 text-center">
            <p className="text-sm font-medium text-slate-600">Loading treatment catalog…</p>
          </div>
        ) : catalog.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/50 py-10 text-center">
            <p className="text-sm font-medium text-slate-600">No catalog treatments available.</p>
          </div>
        ) : (
          catalog.map((row) => {
            const configured = findConfigured(row.id);
            return (
              <TreatmentCard
                key={row.id}
                catalog={row}
                treatment={configured}
                dentists={bookableDentists}
                isDentistAssigned={isDentistAssigned}
                isSavingPrices={savingCatalogId === row.id}
                onSavePrices={(localPrice, foreginPrice) =>
                  handleSavePrices(row, configured, localPrice, foreginPrice)
                }
                onToggleDentist={(doctor) =>
                  configured ? handleToggleDentist(configured, doctor) : undefined
                }
                onDelete={() => configured && setDeleteCandidate(configured.id)}
                onAddSub={() => configured && handleOpenAddSub(configured.id)}
                onEditSub={(sub) => configured && handleOpenEditSub(configured.id, sub)}
                onDeleteSub={(subId) => configured && handleDeleteSubTreatment(configured.id, subId)}
              />
            );
          })
        )}
      </div>

      {bulkConfirmOpen && (
        <ConfirmationModal
          title="Apply one price to all treatments"
          message={`LKR ${bulkLocal} / USD ${bulkForeign} will replace the prices of your ${existingCount} offered treatment(s)${
            newCount > 0
              ? ` and add the other ${newCount} catalog treatment(s) at that price (they stay hidden on BookMyDentist until you assign a dentist)`
              : ""
          }. Continue?`}
          onConfirm={handleBulkApply}
          onCancel={() => setBulkConfirmOpen(false)}
        />
      )}

      {deleteCandidate && (
        <ConfirmationModal
          title="Stop offering this treatment"
          message="Are you sure? This removes the treatment, its prices, and all its sub-treatments from your clinic."
          onConfirm={handleDeleteTreatment}
          onCancel={() => setDeleteCandidate(null)}
        />
      )}

      {isSubModalOpen && (
        <SubTreatmentFormModal
          subTreatment={editingSub}
          onClose={closeSubModal}
          onSave={handleSaveSubTreatment}
        />
      )}
    </section>
  );
};
